import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { createSelector } from 'reselect';

import * as constants from '../helpers/constants/dataTypeConstants';

import browseDataSelectors from './browseDataSelectors';
import {
  getDateSelectionSelectedInboundDate,
  getDateSelectionSelectedOutboundDate,
  getDateSelectionSelectedDate,
  getDateSelectionMinCalendarDate,
  getDateSelectionMinDate,
  getDateSelectionMaxDate,
  getDateSelectionSelectedOutboundMonth,
  getDateSelectionSelectedInboundMonth,
  getDateSelectionSelectedMonth,
  isDateSelectionReturn,
  isDateSelectionWholeYearSelected,
  hasDateSelectionHotelDataOnBothDates,
  getDateSelectionTripDuration,
  getDateSelectionCheapestCombinations,
} from './dateSelectors';
import departureTimeSelectors from './departureTimeSelectors';
import directnessFilterSelectors from './directnessFilterSelectors';
import hotelDateSelectors from './hotelDateSelectors';
import hotelsDatePricesSelectors from './hotelsDatePricesSelectors';
import fromLocalisation from './localisationSelectors';
import monthDataSelectors from './monthDataSelectors';
import routeSelector from './routeSelector';
import staySelectors from './staySelectors';
import uiSelectors from './uiSelectors';

import type { Direction, State } from './types';
import type { DepartureTime } from '../helpers/constants/departureTimeConstants';
import type { Overrides } from '../helpers/data-parsing/gridEntryHelper';

const defaultDirection = constants.OUTBOUND_DIRECTION;

// Date Selectors

export const getSelectedInboundDate = createSelector(
  (state: State) => state.dateSelection,
  (dateSelection) => getDateSelectionSelectedInboundDate(dateSelection),
);

export const getSelectedOutboundDate = createSelector(
  (state: State) => state.dateSelection,
  (dateSelection) => getDateSelectionSelectedOutboundDate(dateSelection),
);

export const getSelectedDate = (
  state: State,
  direction: Direction = defaultDirection,
) => getDateSelectionSelectedDate(state.dateSelection, direction);

export const getMinCalendarDate = (
  state: State,
  direction: Direction = defaultDirection,
) => getDateSelectionMinCalendarDate(state.dateSelection, direction);

export const getMinDate = (state: State) =>
  getDateSelectionMinDate(state.dateSelection);

export const getMaxDate = (state: State) =>
  getDateSelectionMaxDate(state.dateSelection);

export const getSelectedInboundMonth = (state: State) =>
  getDateSelectionSelectedInboundMonth(state.dateSelection);

export const getSelectedOutboundMonth = (state: State) =>
  getDateSelectionSelectedOutboundMonth(state.dateSelection);

export const getSelectedMonth = (
  state: State,
  direction: Direction = defaultDirection,
) => getDateSelectionSelectedMonth(state.dateSelection, direction);

export const isReturn = (state: State) =>
  isDateSelectionReturn(state.dateSelection);

export const isWholeYearSelected = (state: State) =>
  isDateSelectionWholeYearSelected(state.dateSelection);

export const hasHotelDataOnBothDates = (state: State) =>
  hasDateSelectionHotelDataOnBothDates(state.dateSelection);

export const getTripDuration = createSelector(
  (state: State) => state.dateSelection,
  (dateSelection) => getDateSelectionTripDuration(dateSelection),
);

export const getCheapestCombinations = createSelector(
  (state: State) => state.dateSelection,
  (dateSelection) => getDateSelectionCheapestCombinations(dateSelection),
);

export const getTripDays = createSelector(
  getSelectedOutboundDate,
  getSelectedInboundDate,
  (outbound, inbound) =>
    inbound ? differenceInCalendarDays(inbound, outbound) : 0,
);

// Directness Filter Selectors

export const getPreferDirects = (state: State) =>
  directnessFilterSelectors.getPreferDirects(state.directnessFilter);

export const isPreferDirectsEnabled = (state: State) =>
  directnessFilterSelectors.isPreferDirectsEnabled(state.directnessFilter);

export const isPreferDirectsOverridden = (state: State) =>
  directnessFilterSelectors.isPreferDirectsOverridden(state.directnessFilter);

// Departure Time Filter Selectors

export const isBucketIncluded = (state: State, bucket: DepartureTime) =>
  departureTimeSelectors.isBucketIncluded(state.departureTimeFilter, bucket);

export const getDepartureTimesSelected = createSelector(
  (state: State) => state.departureTimeFilter,
  (departureTimeFilter) =>
    departureTimeSelectors.getDepartureTimesSelected(departureTimeFilter),
);

// Hotel Date Selectors

export const getMaxDateForHotel = (state: State) =>
  hotelDateSelectors.getMaxDateForHotel(state.hotelDateSelection);

export const getMinDateForHotel = (state: State) =>
  hotelDateSelectors.getMinDateForHotel(state.hotelDateSelection);

export const getSelectedHotelCheckInDate = (state: State) =>
  hotelDateSelectors.getSelectedCheckInDate(state.hotelDateSelection);

export const getSelectedHotelCheckInMonth = (state: State) =>
  hotelDateSelectors.getSelectedCheckInMonth(state.hotelDateSelection);

export const getSelectedHotelCheckOutDate = (state: State) =>
  hotelDateSelectors.getSelectedCheckOutDate(state.hotelDateSelection);

export const getSelectedHotelCheckOutMonth = (state: State) =>
  hotelDateSelectors.getSelectedCheckOutMonth(state.hotelDateSelection);

// Hotels Date Prices Selectors

export const getCheapestHotelsPriceFromSelection = (
  state: State,
  checkinDate: Date | null,
  checkoutDate: Date | null,
) =>
  hotelsDatePricesSelectors.getCheapestPriceFromSelection(
    state.hotelsDatePrices,
    checkinDate,
    checkoutDate,
  );

export const getHotelsDatesData = (state: State) =>
  hotelsDatePricesSelectors.getDateData(state.hotelsDatePrices);

export const getHotelsDatesLoadingError = (state: State) =>
  hotelsDatePricesSelectors.getError(state.hotelsDatePrices);

export const getTotalHotelsPriceFromSelection = (
  state: State,
  checkinDate: Date,
  checkoutDate: Date,
) =>
  hotelsDatePricesSelectors.getTotalPriceFromSelection(
    state.hotelsDatePrices,
    checkinDate,
    checkoutDate,
  );

export const hasHotelsDatesLoadingError = (state: State) =>
  hotelsDatePricesSelectors.hasError(state.hotelsDatePrices);

export const isHotelsDatesLoading = (state: State) =>
  hotelsDatePricesSelectors.isFetching(state.hotelsDatePrices);

// UI Selectors

export const getSelectedViewMode = (state: State) =>
  uiSelectors.getSelectedViewMode(state.ui);

export const isNoDirectsMessageDismissed = (state: State) =>
  uiSelectors.isNoDirectsMessageDismissed(state.ui);

export const isUserDateSelectionAllowed = (state: State) =>
  uiSelectors.isUserDateSelectionAllowed(state.ui);

// Stay Selectors

export const getStay = createSelector(
  (state: State) => state.stay,
  (stay) => staySelectors.getStay(stay),
);

// Localisation Selectors

export const getTranslatedString = (
  state: State,
  stringId?: string | number | null,
  substitutions?: Record<string, string> | null,
  cardinality?: number,
  maxVariants?: number,
  suffix?: string,
) =>
  fromLocalisation.getTranslatedString(
    state.localisation,
    stringId,
    substitutions,
    cardinality,
    maxVariants,
    suffix,
  );

export const getLocalisationVars = (state: State) =>
  fromLocalisation.getLocalisationVars(state.localisation);

export const getFirstDayOfWeek = (state: State) =>
  fromLocalisation.getFirstDayOfWeek(state.localisation);

// Route Selectors

export const getDestinationId = (state: State) =>
  routeSelector.getDestinationId(state.route);

export const getDestinationEntityId = createSelector(
  (state: State) => state.route,
  (route) => routeSelector.getDestinationEntityId(route),
);

export const getOriginId = (state: State) =>
  routeSelector.getOriginId(state.route);

export const getOriginEntityId = createSelector(
  (state: State) => state.route,
  (route) => routeSelector.getOriginEntityId(route),
);

export const getDestinationCityName = (state: State) =>
  routeSelector.getDestinationCityName(state.route);

// Month Data Selectors

export const getMonthData = (state: State) =>
  monthDataSelectors.getMonthData(state.monthData);

export const isLoading = (state: State) =>
  monthDataSelectors.isLoading(state.monthData);

export const getMonthDataUrl = (state: State) =>
  monthDataSelectors.getMonthDataUrl(state.monthData);

export const isDirectAvailable = (state: State) =>
  monthDataSelectors.hasDirectFlight(state.monthData);

export const getGridPriceData = (
  state: State,
  date: Date,
  direction: Direction = defaultDirection,
  overrides: Overrides = {},
) => monthDataSelectors.getGridPriceData(state, date, direction, overrides);

export const hasDirectsForDate = (
  state: State,
  date: Date,
  direction: Direction = defaultDirection,
  overrides: Overrides = {},
) => monthDataSelectors.hasDirectsForDate(state, date, direction, overrides);

export const getPriceColourClass = (state: State, price: number | null) =>
  monthDataSelectors.getPriceColourClass(state.monthData, price);

export const getPriceTrace = (state: State, key: string | number) =>
  monthDataSelectors.getPriceTrace(state.monthData, key);

// Browse Data Selectors

export const getPriceProviderData = (state: State) =>
  browseDataSelectors.getPriceProviderData(state.browseData);

export const getBaseUrl = (state: State) =>
  browseDataSelectors.getBaseUrl(state.browseData);

export const isInitialLoad = (state: State) =>
  browseDataSelectors.isInitialLoad(state.browseData);

export const isRequestFailureState = (state: State) =>
  browseDataSelectors.isRequestFailureState(state.browseData);

export const getPriceSourceId = (state: State) =>
  browseDataSelectors.getPriceSourceId(state.browseData);

export const getViewId = (state: State) =>
  browseDataSelectors.getViewId(state.browseData);

export const getJhaToken = (state: State) =>
  browseDataSelectors.getJhaToken(state.browseData);
